import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { trackingAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Search, Package, Truck, MapPin, CheckCircle, Clock,
  ArrowRight, FileText
} from 'lucide-react';

const statusSteps = ['dispatched', 'in_transit', 'delivered'];
const statusLabels = { dispatched: 'Dispatched', in_transit: 'In Transit', delivered: 'Delivered' };

const TrackOrder = () => {
  const { toast } = useToast();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async () => {
    if (!trackingNumber.trim()) {
      toast({ title: 'Required', description: 'Enter a tracking number', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const data = await trackingAPI.track(trackingNumber.trim());
      setResult(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
        setResult(null);
      } else {
        toast({ title: 'Error', description: 'Failed to track', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const currentStepIdx = result ? statusSteps.indexOf(result.delivery?.status) : -1;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="track-order-page">
        {/* Header */}
        <div className="text-center pt-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-100 to-teal-100 mb-4">
            <Truck className="h-8 w-8 text-sky-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Track My Order</h1>
          <p className="text-sm text-gray-500 mt-1">Enter your tracking number to see delivery status</p>
        </div>

        {/* Search Box */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Enter tracking number (TRK-xxxx or carrier #)..."
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
                  className="pl-9 h-11"
                  data-testid="tracking-input"
                />
              </div>
              <Button onClick={handleTrack} disabled={loading}
                className="h-11 px-6 bg-gradient-to-r from-sky-500 to-teal-500"
                data-testid="track-btn">
                {loading ? 'Tracking...' : 'Track'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Not Found */}
        {notFound && (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-400" data-testid="not-found">
            <CardContent className="p-5 text-center">
              <Package className="h-10 w-10 text-red-300 mx-auto mb-2" />
              <p className="font-medium text-red-600">Tracking number not found</p>
              <p className="text-sm text-gray-500 mt-1">Double-check the number and try again.</p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4" data-testid="tracking-result">
            {/* Timeline */}
            <Card className="border-0 shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-sky-500 to-teal-500 px-5 py-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white/80 text-xs">Tracking Number</p>
                    <p className="text-white font-mono font-bold text-lg" data-testid="display-tracking">
                      {result.delivery?.tracking_number}
                    </p>
                  </div>
                  <Badge className={
                    result.delivery?.status === 'delivered'
                      ? 'bg-white/20 text-white border-white/30'
                      : 'bg-yellow-400 text-yellow-900'
                  } data-testid="delivery-status">
                    {statusLabels[result.delivery?.status] || result.delivery?.status}
                  </Badge>
                </div>
                {result.delivery?.carrier_tracking_number && (
                  <p className="text-white/60 text-xs mt-1">
                    Carrier: {result.delivery.carrier_tracking_number}
                  </p>
                )}
              </div>

              <CardContent className="p-5">
                {/* Progress Steps */}
                <div className="flex items-center justify-between mb-6" data-testid="timeline">
                  {result.timeline?.map((step, idx) => {
                    const isCompleted = step.completed;
                    const isCurrent = idx === currentStepIdx;
                    return (
                      <React.Fragment key={step.status}>
                        <div className="flex flex-col items-center text-center flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                            isCompleted
                              ? 'bg-green-500 text-white'
                              : isCurrent
                                ? 'bg-sky-500 text-white ring-4 ring-sky-100'
                                : 'bg-gray-100 text-gray-400'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : step.status === 'dispatched' ? (
                              <Package className="h-5 w-5" />
                            ) : step.status === 'in_transit' ? (
                              <Truck className="h-5 w-5" />
                            ) : (
                              <MapPin className="h-5 w-5" />
                            )}
                          </div>
                          <p className={`text-xs font-bold ${isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                            {step.label}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{step.description}</p>
                          {step.date && (
                            <p className="text-[10px] text-gray-500 mt-0.5">{formatDate(step.date)}</p>
                          )}
                        </div>
                        {idx < result.timeline.length - 1 && (
                          <div className={`h-0.5 flex-1 mx-2 rounded ${
                            result.timeline[idx + 1]?.completed ? 'bg-green-400' : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Shipment Details */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Shipment Details</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Shipped From</p>
                    <p className="font-medium text-slate-700">{result.delivery?.warehouse_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Dispatch Date</p>
                    <p className="font-medium text-slate-700">{formatDate(result.delivery?.created_at)}</p>
                  </div>
                  {result.delivery?.delivered_at && (
                    <div>
                      <p className="text-slate-400">Delivered On</p>
                      <p className="font-medium text-green-600">{formatDate(result.delivery.delivered_at)}</p>
                    </div>
                  )}
                  {result.order?.shipping_address && (
                    <div className="col-span-2">
                      <p className="text-slate-400">Shipping Address</p>
                      <p className="font-medium text-slate-700">{result.order.shipping_address}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Items in Shipment</p>
                <div className="space-y-2">
                  {result.delivery?.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border text-sm">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{item.size}</span>
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600">MERV {item.merv}</Badge>
                      </div>
                      <span className="font-bold text-slate-700">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Order Info */}
            {result.order && (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-3">Order Information</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-400">Order #</p>
                      <p className="font-mono font-bold text-slate-700">{result.order.id}</p>
                    </div>
                    {result.order.invoice_number && (
                      <div>
                        <p className="text-slate-400">Invoice #</p>
                        <p className="font-mono text-slate-700">{result.order.invoice_number}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrackOrder;
